import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { searchClients } from '@/features/clients/api/clients';
import { deleteGroup, getGroups, sendGroupSms } from '@/features/communications/api/communications';

export function CommunicationsPage() {
  const [tab, setTab] = useState(0);
  const navigate = useNavigate();
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const [groupId, setGroupId] = useState(0);
  const [confirmSend, setConfirmSend] = useState(false);
  const qc = useQueryClient();
  const groups = useQuery({ queryKey: ['groups'], queryFn: getGroups });
  const clients = useQuery({
    queryKey: ['clients', 'group-options'],
    queryFn: () => searchClients(),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['groups'] });
  const remove = useMutation({
    mutationFn: deleteGroup,
    onSuccess: async () => {
      setSelectedGroupId(null);
      await refresh();
    },
  });
  const send = useMutation({
    mutationFn: () => sendGroupSms(message.trim(), groupId || null),
    onSuccess: () => {
      setMessage('');
      setConfirmSend(false);
    },
  });
  const rows = groups.data ?? [];
  const audience = groupId
    ? (rows.find((group) => group.id === groupId)?.clients ?? [])
    : (clients.data ?? []);
  const segments = Math.max(1, Math.ceil(message.trim().length / 160));
  const estimatedCredits = audience.length * segments;
  return (
    <Box sx={{ p: 1 }}>
      <Paper elevation={0} sx={paperSx}>
        <Box sx={{ px: 2.5, pt: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ sm: 'start' }}
          >
            <Box>
              <Typography variant="h1">Groups & SMS</Typography>
              <Typography color="text.secondary">
                Maintain client audiences and send operational text messages.
              </Typography>
            </Box>
          </Stack>
          <Tabs value={tab} onChange={(_, v: number) => setTab(v)} sx={{ mt: 1 }}>
            <Tab label={`Groups (${rows.length})`} />
            <Tab label="Send SMS" />
          </Tabs>
        </Box>
      </Paper>
      {tab === 0 ? (
        <Paper elevation={0} sx={{ ...paperSx, mt: 1.5, p: 1.5 }}>
          <Stack direction="row" sx={commandSx}>
            {selectedGroupId === null ? (
              <Button
                sx={commandButtonSx}
                startIcon={<AddIcon />}
                onClick={() => void navigate('/communications/groups/new')}
              >
                New Group
              </Button>
            ) : (
              <>
                <Button
                  sx={commandButtonSx}
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => void navigate(`/communications/groups/${selectedGroupId}`)}
                >
                  Edit
                </Button>
                <Button
                  sx={deleteButtonSx}
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => remove.mutate(selectedGroupId)}
                >
                  Delete
                </Button>
              </>
            )}
          </Stack>
          {groups.isError && <Alert severity="error">Groups could not be loaded.</Alert>}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '52px minmax(200px,1fr) minmax(300px,1.5fr)',
            }}
          >
            {['', 'Group name', 'Clients'].map((x, i) => (
              <Typography key={`${x}-${i}`} fontWeight={600} sx={cellSx}>
                {x}
              </Typography>
            ))}
            {rows.map((g) => {
              const selected = selectedGroupId === g.id;
              const select = () => setSelectedGroupId(selected ? null : g.id);
              return (
                <Box
                  key={g.id}
                  sx={{
                    display: 'contents',
                    '& > *': { backgroundColor: selected ? '#eaf3fc' : 'transparent' },
                  }}
                >
                  <Box sx={cellSx}>
                    <Checkbox
                      inputProps={{ 'aria-label': `Select ${g.name}` }}
                      checked={selected}
                      onChange={select}
                    />
                  </Box>
                  <Typography sx={{ ...cellSx, cursor: 'pointer' }} onClick={select}>
                    {g.name}
                  </Typography>
                  <Typography sx={{ ...cellSx, cursor: 'pointer' }} onClick={select}>
                    {g.clients.map((x) => x.name).join(', ') || 'No clients'}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <Typography variant="body2" sx={{ pt: 1.5 }}>
            Rows: {rows.length}
            {selectedGroupId !== null ? '   Selected: 1' : ''}
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ ...paperSx, mt: 1.5, p: 2, maxWidth: 720 }}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            New text message
          </Typography>
          <Stack spacing={2}>
            <TextField
              select
              label="Audience"
              value={groupId}
              onChange={(e) => setGroupId(Number(e.target.value))}
            >
              <MenuItem value={0}>All clients</MenuItem>
              {rows.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name} ({g.clients.length})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              multiline
              minRows={6}
              label="Message"
              helperText={`${message.length}/1600 characters`}
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 1600))}
            />
            <Alert severity="info">
              Audience: {audience.length} client{audience.length === 1 ? '' : 's'} · {segments}{' '}
              message segment{segments === 1 ? '' : 's'} · up to {estimatedCredits} SMS credit
              {estimatedCredits === 1 ? '' : 's'}. Actual recipients depend on saved SMS consent and
              mobile numbers.
            </Alert>
            {send.isError && <Alert severity="error">The message could not be queued.</Alert>}
            {send.isSuccess && <Alert severity="success">Message queued successfully.</Alert>}
            <Button
              variant="contained"
              startIcon={<SendOutlinedIcon />}
              disabled={!message.trim() || send.isPending}
              onClick={() => setConfirmSend(true)}
            >
              Send message
            </Button>
          </Stack>
        </Paper>
      )}
      <Dialog
        open={confirmSend}
        onClose={send.isPending ? undefined : () => setConfirmSend(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Queue this SMS message?</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="warning">
              Sending may use up to {estimatedCredits} SMS credits across {audience.length} selected
              clients. Only contacts with a mobile number and SMS consent should receive it.
            </Alert>
            <Typography sx={{ whiteSpace: 'pre-wrap', bgcolor: '#f3f2f1', p: 1.5 }}>
              {message.trim()}
            </Typography>
            {send.isError && (
              <Alert severity="error">
                The message was not queued. No successful response was received; you can safely
                retry.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSend(false)} disabled={send.isPending}>
            Go back
          </Button>
          <Button
            variant="contained"
            startIcon={<SendOutlinedIcon />}
            disabled={send.isPending || !message.trim()}
            onClick={() => send.mutate()}
          >
            {send.isPending ? 'Queuing…' : 'Confirm and queue'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
const paperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0,0,0,.12)',
};
const commandSx = { minHeight: 44, borderBottom: '1px solid #e1dfdd' };
const commandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'primary.main' },
};
const deleteButtonSx = { color: 'text.primary', '& .MuiButton-startIcon': { color: 'error.main' } };
const cellSx = {
  minHeight: 48,
  px: 1.5,
  py: 1.1,
  borderBottom: '1px solid #edebe9',
  display: 'flex',
  alignItems: 'center',
};
